import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { Stack } from "@mui/system";
import { useAtom } from "jotai/index";
import { enqueueSnackbar } from "notistack";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { PasswordElement, TextFieldElement } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useAuthGroupsQuery } from "@/api/hooks/auth-groups-hooks";
import { useSysCfgCombinedQuery } from "@/api/hooks/sys-cfg-hooks";
import { AAutocompleteElement } from "@/components/a-autocomplete";
import {
  ACC_OS_TP,
  ACC_TP,
  osType,
} from "@/components/config-modals/router-edit-modal/constants";
import CustomSelectOption from "@/components/custom-select-option";
import { ModalActionsButtons } from "@/components/modal-actions-buttons";
import { convertEditFormToUpdateDto, mapSysAccountToEditForm } from "@/lib/accounts-utils";
import { isOwner } from "@/lib/auth-predicates";
import {
  ACCOUNT_RELATION_CONFIGS_NAMES,
  ACCOUNT_RELATION_SERVICE_CONFIGS_NAMES,
  AccountTypeEnum,
  ROOM_NAMES,
} from "@/lib/constants";
import { SysAccountRegularDto, SysAccountUpdateDto } from "@/shared/api";
import { meStore } from "@/store/store";
import { SysAccountEditForm } from "@/types/account-types";

const SysAccountWriteModal = ({
  mode,
  accountType,
  isOpen = false,
  roomName,
  defaultValues,
  onSave,
  onClose,
  confirmLoading = false,
  disabled = false,
}: {
  mode: "add" | "edit";
  accountType: "service" | "regular";
  isOpen?: boolean;
  roomName: string;
  defaultValues?: SysAccountRegularDto;
  onSave: (name: string, account: SysAccountUpdateDto) => void;
  onClose: () => void;
  confirmLoading?: boolean;
  disabled?: boolean;
}) => {
  const { t } = useTranslation();
  const [me] = useAtom(meStore);

  const schema = useMemo(() => {
    return z.object({
      name: z.string().min(1),
      authGroups: isOwner(me)
        ? z.array(z.string())
        : z.array(z.string()).min(1, t("containsAtLeastOneGroup")),
      password: z.string().min(1),
      walletCode: z.string().min(1, t("fieldRequired")).regex(/^\d+$/, t("onlyNumbers")),
      source: z.string(),
      osType: z
        .number({ invalid_type_error: t("fieldRequired") })
        .int()
        .min(0)
        .max(2),
      accountType: z.number(),
      comment: z.string().nullable(),
      gameGroup: z.string().nullable(),
      cid: z.coerce.string().nullable(),
      proxy: z.string().nullable(),
      behaviour: z.string().nullable(),
      schedule: z.string().nullable(),
      gameType: z.string().nullable(),
      aiProfile: z.string().nullable(),
      aiProfileOb: z.string().nullable(),
      timingProfile: z.string().nullable(),
      timingProfileOb: z.string().nullable(),
      insuranceProfile: z.string().nullable(),
      insuranceProfileOb: z.string().nullable(),
    });
  }, [me, t]);

  const { data: authGroupsRs, isLoading: authGroupsIsLoading } = useAuthGroupsQuery({
    enabled: isOpen,
  });

  const authGroups = useMemo(() => {
    return authGroupsRs?.data.map((it) => it.name).sort((a, b) => a.localeCompare(b)) ?? [];
  }, [authGroupsRs]);

  const initAccount = useMemo<SysAccountEditForm>(() => {
    const rs = mapSysAccountToEditForm(defaultValues);

    if (mode === "add" && accountType === "regular") {
      rs.accountType = ACC_TP.REGULAR;
    }

    if (authGroups.length === 1 && !isOwner(me)) {
      rs.authGroups = authGroups;
    }

    return rs;
  }, [accountType, defaultValues, mode, me, authGroups]);

  const accountTypes = useMemo<number[]>(() => {
    if (mode === "edit")
      return [ACC_TP.REGULAR, ACC_TP.MANAGER, ACC_TP.GRABBER, ACC_TP.SCANNER];

    if (mode === "add") {
      return accountType === "service"
        ? [ACC_TP.MANAGER, ACC_TP.SCANNER, ACC_TP.GRABBER]
        : [ACC_TP.REGULAR];
    }

    return [];
  }, [accountType, mode]);

  const { data: selectConfigsRs } = useSysCfgCombinedQuery(
    {
      domain: roomName,
      config_names: ACCOUNT_RELATION_CONFIGS_NAMES,
    },
    {
      enabled: isOpen,
    }
  );

  const { data: selectServiceConfigsRs } = useSysCfgCombinedQuery(
    {
      domain: ROOM_NAMES.SERVICES,
      config_names: ACCOUNT_RELATION_SERVICE_CONFIGS_NAMES,
    },
    {
      enabled: isOpen,
    }
  );

  const configs = useMemo(
    () =>
      new Map(
        (selectConfigsRs?.data ?? [])
          .concat(selectServiceConfigsRs?.data ?? [])
          .map((config) => [
            config.config_name,
            config.configs.sort((a, b) =>
              a.name.localeCompare(b.name, undefined, { numeric: true })
            ),
          ])
      ),
    [selectConfigsRs, selectServiceConfigsRs]
  );

  const {
    control,
    reset,
    handleSubmit,
    setValue,
    formState: { errors, isDirty },
  } = useForm<SysAccountEditForm>({
    mode: "onChange",
    defaultValues: { ...initAccount },
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    reset({ ...initAccount });
  }, [isOpen, initAccount, reset]);

  const submit = handleSubmit((data) => {
    onSave(data.name, convertEditFormToUpdateDto(data));
  });

  const handleSave = () => {
    void submit();
  };

  function handleGeneratePasswordClick(): void {
    const lower = "abcdefghijklmnopqrstuvwxyz";
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const digits = "0123456789";
    const specials = "!@#$%^&*()_-+=<>[]{}|~.:";
    const all = lower + upper + digits + specials;
    const length = 16;

    const get = (chars: string) => chars[Math.floor(Math.random() * chars.length)];
    const password = [
      get(lower),
      get(upper),
      get(digits),
      get(specials),
      ...Array.from({ length: length - 4 }, () => get(all)),
    ];

    const newPassword = password.sort(() => Math.random() - 0.5).join("");

    setValue("password", newPassword);
    navigator.clipboard
      .writeText(newPassword)
      .then(() =>
        enqueueSnackbar(t("Generated password copied to clipboard"), { variant: "info" })
      );
  }

  return (
    <Dialog
      open={isOpen}
      onClose={(event, reason) => {
        if (
          reason === "backdropClick" &&
          isDirty &&
          !window.confirm("Data has been changed. Do you want discard changes?")
        )
          return; //ask question if changes==true and click outSide
        onClose?.();
      }}
    >
      <DialogTitle>
        {mode === "add"
          ? t("createAccount")
          : t("editAccountName", { name: initAccount.name })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ paddingTop: "5px", width: "400px" }}>
          <TextFieldElement
            required
            name="name"
            control={control}
            label={t("name")}
            error={!!errors.name}
            helperText={errors.name?.message ?? " "}
            variant="standard"
            InputProps={{
              disabled: mode === "edit",
            }}
          />

          <AAutocompleteElement
            name="authGroups"
            loading={authGroupsIsLoading}
            control={control}
            label={t("authGroups")}
            options={authGroups}
            multiple
            required={!isOwner(me)}
            textFieldProps={{
              error: !!errors.authGroups,
              helperText: errors.authGroups?.message ?? " ",
              variant: "standard",
            }}
            autocompleteProps={{
              readOnly: disabled || (authGroups.length === 1 && !isOwner(me)),
              renderOption: (props, option, { selected }) => (
                <CustomSelectOption label={option} selected={selected} props={props} />
              ),
            }}
          />

          <Box display="flex" alignItems="center" gap={1}>
            <PasswordElement
              name="password"
              required
              control={control}
              label={t("password")}
              error={!!errors.password}
              helperText={errors.password?.message ?? " "}
              variant="standard"
              InputProps={{
                disabled: disabled,
              }}
              renderIcon={disabled ? () => <></> : undefined}
              sx={{ flexGrow: 1 }}
            />

            {/*<div>
              <IconButton
                onClick={handleGeneratePasswordClick}
                size="small"
                sx={{
                  position: "absolute",
                  right: 83,          // сдвиг левее
                  top: 275,         // сдвиг ниже
                  transform: "translateY(-50%)",
                  border: "1px solid #ccc",
                  width: 24,
                  height: 24,
                  zIndex: 1,
                  borderStyle: "none",
                }}
              >
                <Autorenew fontSize="small" />
              </IconButton>
            </div>*/}
          </Box>

          {disabled ? (
            <PasswordElement
              name="walletCode"
              required
              control={control}
              label={t("walletCode")}
              error={!!errors.walletCode}
              helperText={errors.walletCode?.message ?? " "}
              variant="standard"
              InputProps={{ disabled: disabled }}
              renderIcon={disabled ? () => <></> : undefined}
            />
          ) : (
            <TextFieldElement
              required
              name="walletCode"
              control={control}
              label={t("walletCode")}
              error={!!errors.walletCode}
              helperText={errors.walletCode?.message ?? " "}
              variant="standard"
              InputProps={{ disabled: disabled }}
            />
          )}

          <TextFieldElement
            name="source"
            control={control}
            label={t("source")}
            error={!!errors.source}
            helperText={errors.source?.message ?? " "}
            variant="standard"
            InputProps={{ disabled: disabled }}
          />

          <TextFieldElement
            type="number"
            name="cid"
            control={control}
            label={t("cid")}
            error={!!errors.cid}
            helperText={errors.cid?.message ?? " "}
            variant="standard"
            InputProps={{ disabled: disabled }}
          />

          <AAutocompleteElement
            name="osType"
            control={control}
            label={t("osType")}
            options={roomName === "aa_eco" ? [ACC_OS_TP.IOS] : osType.map((i) => i.value)} //Kostyl asked FF i IS. For AA available only IOS
            autocompleteProps={{
              readOnly: disabled,
              getOptionLabel: (option) => osType.map((i) => i.label)[option] ?? t("unknown"),
              disableClearable: true,
            }}
            textFieldProps={{
              variant: "standard",
              error: !!errors.osType,
              helperText: errors.osType?.message ?? " ",
            }}
          />

          {(mode === "edit" || accountType === "service") && (
            <AAutocompleteElement
              name="accountType"
              control={control}
              label={t("accountType")}
              options={accountTypes}
              autocompleteProps={{
                readOnly: disabled,
                getOptionLabel: (option) => {
                  return AccountTypeEnum[option] ?? t("unknown");
                },
                disableClearable: true,
              }}
              textFieldProps={{
                variant: "standard",
                error: !!errors.accountType,
                helperText: errors.accountType?.message ?? " ",
              }}
            />
          )}

          <AAutocompleteElement
            name="gameGroup"
            control={control}
            label={t("gameGroup")}
            options={configs.get("game_group")?.map((i) => i.name) ?? []}
            autocompleteProps={{
              readOnly: disabled,
            }}
            textFieldProps={{
              variant: "standard",
              error: !!errors.gameGroup,
              helperText: errors.gameGroup?.message ?? " ",
            }}
          />

          <AAutocompleteElement
            name="proxy"
            control={control}
            label={t("proxy")}
            options={configs.get("proxy")?.map((i) => i.name) ?? []}
            autocompleteProps={{
              readOnly: disabled,
            }}
            textFieldProps={{
              variant: "standard",
              error: !!errors.proxy,
              helperText: errors.proxy?.message ?? " ",
            }}
          />

          <AAutocompleteElement
            name="behaviour"
            control={control}
            label={t("behavior")}
            options={configs.get("behaviour")?.map((i) => i.name) ?? []}
            autocompleteProps={{
              readOnly: disabled,
            }}
            textFieldProps={{
              variant: "standard",
              error: !!errors.behaviour,
              helperText: errors.behaviour?.message ?? " ",
            }}
          />

          <AAutocompleteElement
            name="schedule"
            control={control}
            label={t("schedule")}
            options={configs.get("schedule")?.map((i) => i.name) ?? []}
            autocompleteProps={{
              readOnly: disabled,
            }}
            textFieldProps={{
              variant: "standard",
              error: !!errors.schedule,
              helperText: errors.schedule?.message ?? " ",
            }}
          />

          <AAutocompleteElement
            name="gameType"
            control={control}
            label={t("gameType")}
            options={configs.get("game_type")?.map((i) => i.name) ?? []}
            autocompleteProps={{
              readOnly: disabled,
            }}
            textFieldProps={{
              variant: "standard",
              error: !!errors.gameType,
              helperText: errors.gameType?.message ?? " ",
            }}
          />

          <AAutocompleteElement
            name="aiProfile"
            control={control}
            label={t("aiProfile")}
            options={configs.get("ai_profile")?.map((i) => i.name) ?? []}
            autocompleteProps={{
              readOnly: disabled,
            }}
            textFieldProps={{
              variant: "standard",
              error: !!errors.aiProfile,
              helperText: errors.aiProfile?.message ?? " ",
            }}
          />

          <AAutocompleteElement
            name="aiProfileOb"
            control={control}
            label={t("aiProfileOb")}
            options={configs.get("ai_profile")?.map((i) => i.name) ?? []}
            autocompleteProps={{
              readOnly: disabled,
            }}
            textFieldProps={{
              variant: "standard",
              error: !!errors.aiProfileOb,
              helperText: errors.aiProfileOb?.message ?? " ",
            }}
          />

          <AAutocompleteElement
            name="insuranceProfile"
            control={control}
            label={t("insuranceProfile")}
            options={configs.get("insurance")?.map((i) => i.name) ?? []}
            autocompleteProps={{
              readOnly: disabled,
            }}
            textFieldProps={{
              variant: "standard",
              error: !!errors.insuranceProfile,
              helperText: errors.insuranceProfile?.message ?? " ",
            }}
          />

          <AAutocompleteElement
            name="insuranceProfileOb"
            control={control}
            label={t("insuranceProfileOb")}
            options={configs.get("insurance")?.map((i) => i.name) ?? []}
            autocompleteProps={{
              readOnly: disabled,
            }}
            textFieldProps={{
              variant: "standard",
              error: !!errors.insuranceProfileOb,
              helperText: errors.insuranceProfileOb?.message ?? " ",
            }}
          />

          <AAutocompleteElement
            name="timingProfile"
            control={control}
            label={t("timingProfile")}
            options={configs.get("timing")?.map((i) => i.name) ?? []}
            autocompleteProps={{
              readOnly: disabled,
            }}
            textFieldProps={{
              variant: "standard",
              error: !!errors.timingProfile,
              helperText: errors.timingProfile?.message ?? " ",
            }}
          />

          <AAutocompleteElement
            name="timingProfileOb"
            control={control}
            label={t("timingProfileOb")}
            options={configs.get("timing")?.map((i) => i.name) ?? []}
            autocompleteProps={{
              readOnly: disabled,
            }}
            textFieldProps={{
              variant: "standard",
              error: !!errors.timingProfileOb,
              helperText: errors.timingProfileOb?.message ?? " ",
            }}
          />

          {/*{accountType === "service" && (*/}
          <TextFieldElement
            name="comment"
            control={control}
            label={t("comment")}
            error={!!errors.walletCode}
            helperText={errors.walletCode?.message ?? " "}
            variant="standard"
            InputLabelProps={{ disabled: disabled }}
          />
          {/*)}*/}
        </Stack>
      </DialogContent>

      <DialogActions>
        <ModalActionsButtons onCancelClick={onClose} onConfirmClick={handleSave} />
      </DialogActions>
    </Dialog>
  );
};

export default SysAccountWriteModal;
